import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const projects = [
  {
    id: "radius-noida",
    testid: "project-card-radius-noida",
    title: "Radius Company",
    location: "Noida, Uttar Pradesh",
    status: "Completed",
    scope: ["Turnkey Fit-out", "Electrical", "Plumbing", "Furniture"],
    description:
      "Our flagship first project — a complete corporate fit-out covering electrical infrastructure, plumbing systems and custom office furniture for Radius Company.",
    img: "https://images.unsplash.com/photo-1519143009590-e3800b9df468?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwY29uc3RydWN0aW9u&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "rims-manipur",
    testid: "project-card-rims-manipur",
    title: "RIMS Hospital",
    location: "Manipur",
    status: "Completed",
    scope: ["Electrical Infrastructure", "HV Systems"],
    description:
      "Hospital-grade electrical work for RIMS Hospital — critical power systems engineered for zero-failure medical environments.",
    img: "https://customer-assets-lqy194kg.emergentagent.net/job_construction-hub-734/artifacts/zytk6mlq_m2.jpeg",
  },
  {
    id: "uk-realty",
    testid: "project-card-uk-realty",
    title: "UK Realty Developments",
    location: "Multiple Sites",
    status: "Ongoing",
    scope: ["Multi-Project", "Electrical", "Plumbing", "Furniture"],
    description:
      "Currently executing multiple concurrent projects for UK Realty — commercial and residential developments across disciplines.",
    img: "https://customer-assets-lqy194kg.emergentagent.net/job_construction-hub-734/artifacts/eg2h41ss_g2.jpeg",
  },
];

const filters = ["All", "Ongoing", "Completed"];

export const Projects = () => {
  const [filter, setFilter] = useState("All");
  const visible = projects.filter((p) => filter === "All" || p.status === filter);

  return (
    <section id="projects" className="scroll-mt-24 bg-[#0B0C0E] py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Key projects</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-[#F3F4F6] sm:text-5xl">
              Work that speaks
              <br />
              in concrete.
            </h2>
          </div>
          <div className="flex gap-2" data-testid="project-filters">
            {filters.map((f) => (
              <button
                key={f}
                data-testid={`project-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-5 py-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors ${
                  filter === f
                    ? "bg-[#FFFFFF] text-black"
                    : "border border-white/15 text-[#9CA3AF] hover:border-[#FFFFFF] hover:text-[#FFFFFF]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid gap-6 md:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                data-testid={p.testid}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="group border border-[#2E333D] bg-[#1A1D24] transition-colors duration-300 hover:border-[#FFFFFF]/60"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={p.img}
                    alt={`${p.title} — ${p.location}`}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span
                    className={`absolute left-4 top-4 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
                      p.status === "Ongoing" ? "bg-[#FFFFFF] text-black" : "bg-black/80 text-[#F3F4F6] backdrop-blur"
                    }`}
                  >
                    {p.status}
                  </span>
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">
                    <MapPin size={12} className="text-[#FFFFFF]" /> {p.location}
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight text-[#F3F4F6]">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#9CA3AF]">{p.description}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.scope.map((s) => (
                      <span key={s} className="border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal className="mt-20">
          <p className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-[#FFFFFF]">
            Site diaries · real photos from our live projects
          </p>
          <div className="grid gap-4 sm:grid-cols-3" data-testid="site-gallery">
            {[
              ["https://customer-assets-lqy194kg.emergentagent.net/job_construction-hub-734/artifacts/6h6lv0gc_g1.jpeg", "Gativan crew tying reinforcement steel on a high-rise slab"],
              ["https://customer-assets-lqy194kg.emergentagent.net/job_construction-hub-734/artifacts/ezvrb6yf_g3.jpeg", "Top view of slab reinforcement grid executed by Gativan"],
              ["https://customer-assets-lqy194kg.emergentagent.net/job_construction-hub-734/artifacts/uquy3oce_g5.jpeg", "Gativan slab formwork and rebar work in progress"],
            ].map(([src, alt], i) => (
              <figure key={src} className="group relative overflow-hidden border border-[#2E333D]" data-testid={`site-photo-${i + 1}`}>
                <img
                  src={src}
                  alt={alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute bottom-3 left-3 bg-black/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFFFFF] backdrop-blur">
                  Site {String(i + 1).padStart(2, "0")}
                </span>
              </figure>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
